import React from "react";
import { toast } from "sonner";
import { useQuery } from "@tanstack/react-query";
import { Gift, Share2, Star, Users, Check, Sparkles } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import { redeemLoyalty } from "@/lib/api";

const EARN = [
  { action: "share", label: "Share a look", pts: 25, Icon: Share2 },
  { action: "review", label: "Write a review", pts: 50, Icon: Star },
  { action: "referral", label: "Refer a friend", pts: 250, Icon: Users },
];
const REWARDS = [
  { reward: "10% off next order", cost: 200 },
  { reward: "Free shipping", cost: 300 },
  { reward: "$25 off", cost: 500 },
  { reward: "Early drop access", cost: 750 },
];

export default function Rewards() {
  const { guestId, loyalty, refreshLoyalty, earn } = useStore();
  const { refetch } = useQuery({ queryKey: ["loyalty", guestId], queryFn: refreshLoyalty, enabled: false });

  const doEarn = async (action) => {
    await earn(action);
    toast.success(`Points added — keep climbing.`);
  };
  const doRedeem = async (r) => {
    try {
      const res = await redeemLoyalty({ guest_id: guestId, reward: r.reward, cost: r.cost });
      await refreshLoyalty();
      toast.success(`Unlocked "${r.reward}" · Code ${res.code}`, { duration: 6000 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Not enough points yet");
    }
  };

  if (!loyalty) return <div className="min-h-[60vh] flex items-center justify-center font-body text-black/40">Loading rewards…</div>;

  return (
    <div className="bg-[#0A0A0A] text-[#F4F2ED]">
      <section className="relative grain">
        <div className="relative z-10 px-5 sm:px-8 lg:px-12 py-16 sm:py-24">
          <p className="overline text-[#FFFFFF]">LEVINCHI Rewards</p>
          <h1 className="font-hero text-6xl sm:text-8xl mt-3 leading-none">EARN YOUR<br />STATUS</h1>
        </div>
      </section>

      {/* Points card */}
      <section className="px-5 sm:px-8 lg:px-12 pb-16">
        <div className="border border-white/15 p-8 sm:p-12 grid md:grid-cols-2 gap-10 items-center" data-testid="points-card">
          <div>
            <p className="overline text-white/50">Your balance</p>
            <div className="font-hero text-7xl sm:text-8xl text-[#FFFFFF] leading-none mt-2" data-testid="points-balance">{loyalty.points}</div>
            <p className="font-mono text-xs text-white/40 mt-2">points · {loyalty.tier.name} tier</p>
          </div>
          <div>
            {loyalty.next_tier ? (
              <>
                <div className="flex justify-between overline text-white/60 mb-2">
                  <span>{loyalty.tier.name}</span><span>{loyalty.next_tier.name}</span>
                </div>
                <div className="h-2 bg-white/10"><div className="h-full bg-[#FFFFFF] transition-all duration-700" style={{ width: `${loyalty.progress}%` }} /></div>
                <p className="font-body text-sm text-white/60 mt-3">{loyalty.to_next} points to <b className="text-[#F4F2ED]">{loyalty.next_tier.name}</b> — {loyalty.next_tier.perk}.</p>
              </>
            ) : (
              <p className="font-body text-[#FFFFFF]">You've reached the top tier. Icon status: unlocked.</p>
            )}
          </div>
        </div>
      </section>

      {/* Tiers */}
      <section className="px-5 sm:px-8 lg:px-12 pb-16">
        <h2 className="font-hero text-4xl mb-6">THE TIERS</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {loyalty.tiers.map((t) => {
            const active = loyalty.tier.name === t.name;
            return (
              <div key={t.name} data-testid={`tier-${t.name}`} className={`p-6 border ${active ? "border-[#FFFFFF] bg-white/5" : "border-white/10"}`}>
                <div className="flex items-center justify-between">
                  <span className="font-display uppercase tracking-widest text-sm">{t.name}</span>
                  {active && <span className="overline text-[#FFFFFF]">You</span>}
                </div>
                <p className="font-mono text-[11px] text-white/40 mt-1">{t.min}+ pts</p>
                <p className="font-body text-sm text-white/70 mt-4">{t.perk}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Earn */}
      <section className="px-5 sm:px-8 lg:px-12 pb-16">
        <h2 className="font-hero text-4xl mb-6">WAYS TO EARN</h2>
        <div className="grid sm:grid-cols-3 gap-3">
          {EARN.map(({ action, label, pts, Icon }) => (
            <button key={action} onClick={() => doEarn(action)} data-testid={`earn-${action}`}
                    className="p-6 border border-white/10 hover:border-[#FFFFFF] transition-colors text-left group">
              <Icon size={22} strokeWidth={1.25} className="text-[#FFFFFF]" />
              <p className="font-display uppercase tracking-wide text-sm mt-4">{label}</p>
              <p className="font-mono text-xs text-white/50 mt-1 group-hover:text-[#FFFFFF]">+{pts} points</p>
            </button>
          ))}
        </div>
      </section>

      {/* Redeem */}
      <section className="px-5 sm:px-8 lg:px-12 pb-24">
        <h2 className="font-hero text-4xl mb-6">REDEEM</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {REWARDS.map((r) => {
            const can = loyalty.points >= r.cost;
            return (
              <div key={r.reward} className="p-6 border border-white/10 flex flex-col">
                <Gift size={20} strokeWidth={1.25} className={can ? "text-[#FFFFFF]" : "text-white/30"} />
                <p className="font-display uppercase tracking-wide text-sm mt-4">{r.reward}</p>
                <p className="font-mono text-xs text-white/50 mt-1">{r.cost} points</p>
                <button onClick={() => doRedeem(r)} disabled={!can} data-testid={`redeem-${r.cost}`}
                        className={`mt-5 py-3 overline transition-colors ${can ? "bg-[#FFFFFF] text-[#0A0A0A] hover:bg-[#FFFFFF]" : "border border-white/15 text-white/30 cursor-not-allowed"}`}>
                  {can ? "Redeem" : "Locked"}
                </button>
              </div>
            );
          })}
        </div>
      </section>

      {loyalty.history?.length > 0 && (
        <section className="px-5 sm:px-8 lg:px-12 pb-24">
          <h2 className="font-hero text-3xl mb-5">ACTIVITY</h2>
          <div className="border border-white/10 divide-y divide-white/10">
            {loyalty.history.map((h, i) => (
              <div key={i} className="flex items-center justify-between px-5 py-3.5">
                <span className="font-body text-sm text-white/70 flex items-center gap-2"><Check size={13} className="text-[#FFFFFF]" />{h.reason}</span>
                <span className={`font-mono text-sm ${h.points >= 0 ? "text-[#FFFFFF]" : "text-white/40"}`}>{h.points >= 0 ? "+" : ""}{h.points}</span>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
